import { normalizeTuneProjection, resolveProjectionRequest, tuneProjectionInit } from "@common/talkn-config";
import type { ProjectionRequest, TuneProjection } from "@common/talkn-config";
import {
  CommandPayloadMap,
  ResponseType,
  StoresDispatcher,
  createStoresDispatcher,
} from '@api-client/wssWorker/StoresDispatcher';
import { getTalknUserId } from '@api-client/utils/userSession';
import trackingService from '@api-client/tracking/TrackingService';
import type ApiState from './state';

// TalknAPIの初期化パラメータ
export type TalknAPIParams = {
  tuneOption?: Partial<TuneProjection>;
  rankScope?: string;
  tuneRequest?: Partial<ProjectionRequest>;
  apiKey?: string;
  host?: string;
  protocol?: string;
  onChangeState?: (apiState: ApiState) => void;
  isTuneSameCh?: boolean;
  isTuneMultiCh?: boolean;
  isDebug?: boolean;
  /**
   * 接続モード:
   *   'tune'        — デフォルト。独立接続、LIVE数+1（ch-admin simulator 用途等）
   *   'tune-shared' — 同一CH間で接続を共有し LIVE+1 は1回のみ（widget 標準）
   *   'observe'     — LIVE数に影響しない購読接続（ranking/badge 等の表示 widget）
   */
  connectionMode?: 'tune' | 'tune-shared' | 'observe';
};

type SharedEntry = {
  promise: Promise<ScopedTalknClient>;
  refCount: number;
};

// tune-shared / observe の共有接続 Map（キー: "${mode}:${connection}"）
const sharedConnections = new Map<string, SharedEntry>();

// digChのパラメータ
export type DigChParams = {
  maxDepth?: number;
  maxLinksPerPage?: number;
  maxTotalLinks?: number;
  subscribeDiscovered?: boolean;
  basicAuth?: {
    user: string;
    pass: string;
  };
};

// tuneIdスコープされたAPIクライアント
export interface ScopedTalknClient {
  getState(): ApiState;
  fetchPosts(): Promise<ResponseType<CommandPayloadMap['fetchPosts']>>;
  fetchRank(): Promise<ResponseType<CommandPayloadMap['fetchRank']>>;
  fetchDetail(): Promise<ResponseType<CommandPayloadMap['fetchChDetail']>>;
  discoverCh(params?: Omit<DigChParams, 'subscribeDiscovered'>): Promise<ResponseType<CommandPayloadMap['discoverCh']>>;
  digCh(params?: DigChParams): Promise<ResponseType<CommandPayloadMap['digCh']>>;
  subscribeDiscoveredChs(connections: string[]): Promise<ResponseType<CommandPayloadMap['subscribeDiscoveredChs']>>;
  post(content: string, userName?: string, userAvatar?: string, replyToId?: string): Promise<ResponseType<CommandPayloadMap['post']>>;
  deletePost(uid: string, replyToId: string, content: string): Promise<ResponseType<CommandPayloadMap['deletePost']>>;
  getClientUid(): string;
  subscribe(callback: (apiState: ApiState) => void): () => void;
  startTyping(userName?: string): void;
  stopTyping(): void;
  retune(connection: string): Promise<ResponseType<CommandPayloadMap['tune']>>;
  untune(): Promise<ResponseType<CommandPayloadMap['untune']>>;
  trackCardView(ch: string): void;
  trackCardClick(ch: string, linkUrl: string): void;
}

// tuneIdスコープされたAPIクライアントの実装
class ScopedTalknClientImpl implements ScopedTalknClient {
  private tuneId: string;
  private storesDispatcher: StoresDispatcher;
  private connection: string;
  private talknUserId: string;
  private socketSessionId?: string;
  private isUntuned = false;
  private readonly handlePageHide: () => void;
  private readonly handleBeforeUnload: () => void;

  constructor(
    tuneId: string,
    connection: string,
    storesDispatcher: StoresDispatcher,
    socketSessionId?: string
  ) {
    this.tuneId = tuneId;
    this.connection = connection;
    this.storesDispatcher = storesDispatcher;
    this.socketSessionId = socketSessionId;
    // メインスレッドでlocalStorageからユーザーIDを取得
    // WebWorker内では localStorage にアクセスできないため、ここで取得してWorkerに渡す
    this.talknUserId = getTalknUserId();
    this.handlePageHide = this.cleanupOnPageClose.bind(this);
    this.handleBeforeUnload = this.cleanupOnPageClose.bind(this);

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    }
  }

  getState(): ApiState {
    return this.storesDispatcher.getState(this.tuneId);
  }

  async fetchPosts(): Promise<ResponseType<CommandPayloadMap['fetchPosts']>> {
    return this.storesDispatcher.fetchPosts(this.tuneId);
  }

  async fetchRank(): Promise<ResponseType<CommandPayloadMap['fetchRank']>> {
    return this.storesDispatcher.fetchRank(this.tuneId);
  }

  async fetchDetail(): Promise<ResponseType<CommandPayloadMap['fetchChDetail']>> {
    return this.storesDispatcher.fetchDetail(this.tuneId);
  }

  async discoverCh(
    params?: Omit<DigChParams, 'subscribeDiscovered'>
  ): Promise<ResponseType<CommandPayloadMap['discoverCh']>> {
    return this.storesDispatcher.discoverCh(this.tuneId, params);
  }

  async digCh(params?: DigChParams): Promise<ResponseType<CommandPayloadMap['digCh']>> {
    return this.storesDispatcher.digCh(this.tuneId, params);
  }

  async subscribeDiscoveredChs(
    connections: string[]
  ): Promise<ResponseType<CommandPayloadMap['subscribeDiscoveredChs']>> {
    return this.storesDispatcher.subscribeDiscoveredChs(this.tuneId, connections);
  }

  async post(content: string, userName?: string, userAvatar?: string, replyToId?: string): Promise<ResponseType<CommandPayloadMap['post']>> {
    return this.storesDispatcher.post(content, this.tuneId, userName, userAvatar, replyToId);
  }

  async deletePost(uid: string, replyToId: string, content: string): Promise<ResponseType<CommandPayloadMap['deletePost']>> {
    return this.storesDispatcher.deletePost(uid, replyToId, content, this.tuneId);
  }

  getClientUid(): string {
    return this.storesDispatcher.uid;
  }

  subscribe(callback: (apiState: ApiState) => void): () => void {
    return this.storesDispatcher.subscribeToTuneId(this.tuneId, callback);
  }

  startTyping(userName?: string): void {
    this.storesDispatcher.sendTyping(true, this.tuneId, userName);
  }

  stopTyping(): void {
    this.storesDispatcher.sendTyping(false, this.tuneId);
  }

  async retune(connection: string): Promise<ResponseType<CommandPayloadMap['tune']>> {
    return this.storesDispatcher.retune(this.tuneId, connection);
  }

  async untune(): Promise<ResponseType<CommandPayloadMap['untune']>> {
    if (this.isUntuned) {
      try {
        return Promise.resolve({ state: this.storesDispatcher.getState(this.tuneId), tuneId: this.tuneId });
      } catch {
        return Promise.resolve({ state: {} as ApiState, tuneId: this.tuneId });
      }
    }

    this.isUntuned = true;
    this.removeLifecycleListeners();
    await trackingService.flushPending(this.tuneId);
    trackingService.release(this.tuneId);
    return this.storesDispatcher.untune(this.connection);
  }

  trackCardView(ch: string): void {
    this.storesDispatcher.trackCardEvent(
      this.tuneId,
      'card_view',
      ch,
      this.connection,
      undefined,
      this.talknUserId,
      this.socketSessionId
    );
  }

  trackCardClick(ch: string, linkUrl: string): void {
    this.storesDispatcher.trackCardEvent(
      this.tuneId,
      'card_click',
      ch,
      this.connection,
      linkUrl,
      this.talknUserId,
      this.socketSessionId
    );
  }

  private cleanupOnPageClose(): void {
    if (this.isUntuned) {
      return;
    }

    this.isUntuned = true;
    this.removeLifecycleListeners();

    void trackingService.flushPending(this.tuneId).catch(() => {});
    trackingService.release(this.tuneId);

    // unload中はawaitできないため、切断要求だけ即時に投げる
    void this.storesDispatcher.untune(this.connection).catch(() => {});
  }

  private removeLifecycleListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }
}

/**
 * TalknAPI - WebSocket接続を確立し、指定されたチャンネルにtuneする
 *
 * @param connection - 接続先チャンネル（例: "/", "/topic/", etc.）
 * @param params - オプションパラメータ
 * @param params.tuneOption - 購読オプション（posts, rank, rankAllなど）
 * @param params.onChangeState - 状態変更時のコールバック
 * @param params.isTuneSameCh - 同一チャンネルへの複数接続を許可するか
 * @param params.isTuneMultiCh - 複数チャンネルへの接続を許可するか
 * @param params.isDebug - デバッグモード有効化
 * @returns ScopedTalknClient - チャンネル固有のAPIクライアント
 *
 * @example
 * ```typescript
 * const client = await TalknAPI("/", {
 *   tuneOption: { posts: true, rank: true },
 *   isDebug: true
 * });
 *
 * client.subscribe((state) => {
 *   console.log("State updated:", state);
 * });
 * ```
 */
async function createTalknClient(
  connection: string,
  params?: TalknAPIParams
): Promise<ScopedTalknClient> {
  const {
    tuneOption = {},
    rankScope,
    tuneRequest,
    apiKey,
    host,
    protocol,
    onChangeState,
    isTuneSameCh = true,
    isTuneMultiCh = true,
    isDebug = false,
    connectionMode = 'tune',
  } = params || {};

  const isObserver = connectionMode === 'observe';

  const storesDispatcher = await createStoresDispatcher({
    isTuneSameCh,
    isTuneMultiCh,
    isDebug,
  });

  const mergedTuneOption: TuneProjection = normalizeTuneProjection({
    ...tuneProjectionInit,
    ...tuneOption,
  });
  const resolvedTuneRequest = resolveProjectionRequest({
    tuneOption: tuneRequest?.tuneOption ?? mergedTuneOption,
    rankScope: tuneRequest?.rankScope ?? rankScope,
  });

  const response = await storesDispatcher.tune(
    connection,
    resolvedTuneRequest.tuneOption,
    onChangeState,
    resolvedTuneRequest.rankScope,
    apiKey,
    host,
    protocol,
    isObserver
  );

  if (!response.tuneId || response.payload?.success === false || response.payload?.error) {
    throw new Error(`Failed to tune to connection: ${connection}`);
  }

  const tuneId = response.tuneId;
  const talknUserId = getTalknUserId();
  const socketSessionId =
    typeof response.payload?.sessionId === 'string' ? response.payload.sessionId : undefined;

  await trackingService.ensureStarted(tuneId, talknUserId, socketSessionId, storesDispatcher);

  return new ScopedTalknClientImpl(tuneId, connection, storesDispatcher, socketSessionId);
}

/**
 * refCount をインクリメントせずに共有接続の untune を呼ぶ。
 * widgets/index.ts の MutationObserver コールバックから使用する。
 */
export async function releaseSharedConnection(connection: string, connectionMode: 'tune-shared' | 'observe'): Promise<void> {
  const key = `${connectionMode}:${connection}`;
  const entry = sharedConnections.get(key);
  if (entry) {
    await entry.promise.then((client) => client.untune()).then(() => undefined).catch(() => undefined);
  }
}

export async function TalknAPI(
  connection: string,
  params?: TalknAPIParams
): Promise<ScopedTalknClient> {
  const connectionMode = params?.connectionMode ?? 'tune';

  if (connectionMode === 'tune') {
    return createTalknClient(connection, params);
  }

  // tune-shared / observe: 同一 key で接続を共有し参照カウント管理
  const key = `${connectionMode}:${connection}`;
  const existing = sharedConnections.get(key);
  if (existing) {
    existing.refCount++;
    return existing.promise;
  }

  const promise = createTalknClient(connection, params).then((client) => {
    const _untune = client.untune.bind(client);
    client.untune = async () => {
      const entry = sharedConnections.get(key);
      if (entry) {
        entry.refCount--;
        if (entry.refCount <= 0) {
          sharedConnections.delete(key);
          return _untune();
        }
      }
      try {
        return { state: client.getState(), tuneId: '' } as Awaited<ReturnType<typeof _untune>>;
      } catch {
        return { state: {} as ApiState, tuneId: '' } as Awaited<ReturnType<typeof _untune>>;
      }
    };
    return client;
  });

  sharedConnections.set(key, { promise, refCount: 1 });
  promise.catch(() => sharedConnections.delete(key));

  return promise;
}

export default TalknAPI;
